import * as React from "react"
import { LayoutGrid } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { useUser } from "@/context/UserContext"
import { useCatalog } from "@/features/integrations/hooks/useCatalog"
import { deriveStatus } from "@/features/integrations/components/statusModel"
import type { UiStatus } from "@/features/integrations/api/types"
import { useBookingT } from "@/hooks/use-translations"
import { HeroSection } from "./HeroSection"
import { SearchBar } from "./SearchBar"
import { CategoryTabs } from "./CategoryTabs"
import { FeaturedSection } from "./FeaturedSection"
import { IntegrationGrid } from "./IntegrationGrid"
import { EmptyState } from "./EmptyState"
import { RequestIntegrationModal } from "./RequestIntegrationModal"

const ALL = "all"
type StatusFilter = "all" | "connected" | "available"

type Props = {
  onOpenDetail: (id: string) => void
}

function readUrlState(): { q: string; category: string; status: StatusFilter } {
  const params = new URLSearchParams(window.location.search)
  const status = params.get("status")
  return {
    q: params.get("q") ?? "",
    category: params.get("category") ?? ALL,
    status: status === "connected" || status === "available" ? status : "all",
  }
}

function writeUrlState(q: string, category: string, status: StatusFilter): void {
  const params = new URLSearchParams(window.location.search)
  if (q) params.set("q", q)
  else params.delete("q")
  if (category !== ALL) params.set("category", category)
  else params.delete("category")
  if (status !== "all") params.set("status", status)
  else params.delete("status")
  const search = params.toString()
  const url = window.location.pathname + (search ? `?${search}` : "")
  window.history.replaceState(null, "", url)
}

function statusMatchesFilter(uiStatus: UiStatus, filter: StatusFilter): boolean {
  if (filter === "all") return true
  if (filter === "connected") {
    return uiStatus === "connected" || uiStatus === "warning" || uiStatus === "error"
  }
  return uiStatus === "available" || uiStatus === "pricing_locked" || uiStatus === "coming_soon"
}

export function CatalogView({ onOpenDetail }: Props) {
  const t = useBookingT()
  const { user } = useUser()
  const isGuest = !user
  const initial = React.useMemo(readUrlState, [])
  const [query, setQuery] = React.useState(initial.q)
  const [debouncedQuery, setDebouncedQuery] = React.useState(initial.q)
  const [category, setCategory] = React.useState(initial.category)
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>(initial.status)
  const [requestOpen, setRequestOpen] = React.useState(false)

  const { isLoading, error, items, categories, connectionsById } = useCatalog()

  // Debounce search input
  React.useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query), 200)
    return () => window.clearTimeout(id)
  }, [query])

  // Sync URL on filter change
  React.useEffect(() => {
    writeUrlState(debouncedQuery, category, statusFilter)
  }, [debouncedQuery, category, statusFilter])

  const filteredItems = React.useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase()
    return items.filter((item) => {
      if (category !== ALL && item.category !== category) return false
      if (q && !item.name.toLowerCase().includes(q)) return false
      const uiStatus = deriveStatus(item, connectionsById[item.id])
      if (!statusMatchesFilter(uiStatus, statusFilter)) return false
      return true
    })
  }, [items, debouncedQuery, category, statusFilter, connectionsById])

  const featuredItems = React.useMemo(() => {
    if (debouncedQuery || category !== ALL || statusFilter !== "all") return []
    return items.filter((i) => i.featured).slice(0, 6)
  }, [items, debouncedQuery, category, statusFilter])

  const featuredIds = React.useMemo(() => new Set(featuredItems.map((f) => f.id)), [featuredItems])

  const gridItems = React.useMemo(
    () => filteredItems.filter((i) => !featuredIds.has(i.id)),
    [filteredItems, featuredIds],
  )

  const resetFilters = () => {
    setQuery("")
    setCategory(ALL)
    setStatusFilter("all")
  }

  if (error) {
    return (
      <div className="rounded-lg border border-error-500 bg-error-100 p-6 text-sm text-error-700">
        {t("integrations_load_error", "Failed to load integrations")}: {error.message}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <HeroSection onRequestIntegration={() => setRequestOpen(true)} />

      <div className="flex flex-col gap-3 max-md:items-stretch md:flex-row md:items-center md:justify-between">
        <SearchBar value={query} onChange={setQuery} className="md:w-80" />
        {!isGuest && (
        <div className="inline-flex items-center gap-1 rounded-md border border-input bg-white p-1">
          {(["all", "connected", "available"] as const).map((opt) => {
            const labelKey =
              opt === "all"
                ? "integrations_filter_status_all"
                : opt === "connected"
                  ? "integrations_filter_connected"
                  : "integrations_filter_available"
            const fallback = opt === "all" ? "All" : opt === "connected" ? "Connected" : "Available"
            const isActive = statusFilter === opt
            return (
              <button
                key={opt}
                type="button"
                onClick={() => setStatusFilter(opt)}
                className={
                  isActive
                    ? "rounded-sm bg-brand-500 px-3 py-1 text-sm text-white"
                    : "rounded-sm px-3 py-1 text-sm text-neutral-700 hover:bg-neutral-100"
                }
              >
                {t(labelKey, fallback)}
              </button>
            )
          })}
        </div>
        )}
      </div>

      {isLoading ? (
        <CatalogSkeleton />
      ) : (
        <>
          <CategoryTabs
            categories={categories}
            active={category}
            onChange={setCategory}
          />

          {filteredItems.length === 0 ? (
            <EmptyState onReset={resetFilters} />
          ) : (
            <>
              {featuredItems.length > 0 && (
                <FeaturedSection
                  items={featuredItems}
                  connectionsById={connectionsById}
                  onSelect={onOpenDetail}
                />
              )}
              {gridItems.length > 0 && (
                <section className="space-y-3">
                  <div className="flex items-center gap-2">
                    <LayoutGrid className="size-4 text-neutral-700" />
                    <h2 className="text-lg font-semibold text-neutral-900">
                      {t("integrations_all_title", "All integrations")}
                    </h2>
                    <span className="text-sm text-neutral-700">({gridItems.length})</span>
                  </div>
                  <IntegrationGrid
                    items={gridItems}
                    connectionsById={connectionsById}
                    onSelect={onOpenDetail}
                  />
                </section>
              )}
            </>
          )}
        </>
      )}

      <RequestIntegrationModal open={requestOpen} onOpenChange={setRequestOpen} />
    </div>
  )
}

function CatalogSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-24 rounded-full" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className="h-40 rounded-lg" />
        ))}
      </div>
    </div>
  )
}
